import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Linkedin, Instagram, Youtube, Facebook, Twitter, Mail, ExternalLink, ArrowUp, LogIn, type LucideProps } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocalePath } from '@/hooks/useLocalePath';
import greeninLogoDark from '@/assets/greenin-impact-logo-dark.svg';
import greeninLogoLight from '@/assets/greenin-impact-logo-light.svg';

const TikTokIcon = (props: LucideProps) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const Footer = () => {
  const { t, language } = useLanguage();
  const { resolvedTheme } = useTheme();
  const lp = useLocalePath();
  
  const logo = resolvedTheme === 'dark' ? greeninLogoDark : greeninLogoLight;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/greenincompany/', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://www.instagram.com/greeninai/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/greeninai/', label: 'Facebook' },
    { icon: Youtube, href: 'https://www.youtube.com/@GreenInAI', label: 'YouTube' },
    { icon: Twitter, href: 'https://x.com/GreenInAI', label: 'X' },
    { icon: TikTokIcon, href: 'https://www.tiktok.com/@greeninai', label: 'TikTok' },
  ];

  return (
    <footer className="bg-card border-t border-border" role="contentinfo">
      <div className="container mx-auto px-4 py-12">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={logo}
                alt={t('common.logoAlt')}
                className="h-14 md:h-16 w-auto max-w-[240px] md:max-w-[280px]"
                width={280}
                height={64}
              />
            </div>
            <p className="text-foreground font-medium mb-2">
              {t('footer.tagline')}
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              {t('footer.line')}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {t('footer.reporting')}
            </p>
            <div className="flex items-center gap-3 text-primary">
              <Mail className="w-4 h-4" aria-hidden="true" />
              <a 
                href="mailto:info@greeninco.com" 
                className="text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
              >
                info@greeninco.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Links</h4>
            <ul className="space-y-2">
              <li>
                <a href="#programs" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('nav.programs')}
                </a>
              </li>
              <li>
                <a href="#ai-good" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('nav.aiForGood')}
                </a>
              </li>
              <li>
                <a href="#transparency" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('nav.transparency')}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('nav.contact')}
                </a>
              </li>
              <li>
                <Link to={lp('/library')} className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('common.knowledgeVault')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Ecosystem */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Ecosystem</h4>
            <ul className="space-y-2">
              <li>
                <a href="https://www.greeninco.com" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center gap-1.5 underline underline-offset-2 decoration-border hover:decoration-primary">
                  GreenIn Company
                  <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a href="https://www.greenin.ai" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center gap-1.5 underline underline-offset-2 decoration-border hover:decoration-primary">
                  GreenIn AI
                  <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a href="https://www.greeninshop.com" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center gap-1.5 underline underline-offset-2 decoration-border hover:decoration-primary">
                  GreenIn Shop
                  <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a href="https://www.greeninacademy.com" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center gap-1.5 underline underline-offset-2 decoration-border hover:decoration-primary">
                  GreenIn Academy
                  <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link to={lp('/privacy')} className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to={lp('/terms')} className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link to={lp('/cookies')} className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('footer.cookies')}
                </Link>
              </li>
              <li>
                <Link to={lp('/accessibility')} className="text-sm text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded min-h-[44px] inline-flex items-center">
                  {t('footer.accessibility')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Accessibility commitment */}
        <div className="mb-8 p-4 bg-muted/30 rounded-lg border border-border/50">
          <p className="text-sm text-muted-foreground">
            {t('common.accessibilityCommitment')}
            {' '}
            <Link to={lp('/accessibility')} className="text-primary underline underline-offset-2 hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-ring rounded">
              {t('common.learnMoreAccessibility')}
              <ExternalLink className="w-3 h-3 inline ml-1" aria-hidden="true" />
            </Link>
          </p>
        </div>

        {/* Back to Top Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={scrollToTop}
            className="group flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background min-h-[48px]"
            aria-label={t('common.backToTop')}
          >
            <ArrowUp className="w-4 h-4 transition-transform group-hover:-translate-y-1" aria-hidden="true" />
            <span className="text-sm font-medium">
              {t('common.backToTop')}
            </span>
          </button>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-border/50">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © 2026 GreenIn Company. All rights reserved.
            </p>
            {/* Admin Sign In - Premium subtle button */}
            <Link
              to="/admin/login"
              className="group inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-primary border border-transparent hover:border-primary/20 hover:bg-primary/5 rounded-full px-4 py-2 min-h-[44px] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
              aria-label={t('common.signIn')}
            >
              <LogIn className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
              <span className="tracking-wide">{t('common.signIn')}</span>
            </Link>
          </div>
          
          {/* Social Links */}
          <div className="flex items-center gap-3 sm:gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-11 h-11 rounded-full bg-muted/50 flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
              >
                <social.icon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
